import React, { useState, useEffect, useCallback } from 'react';
import { Gate } from './AccessStudio';

// Attendance tab: teachers mark Present/Absent per child each morning. Stored
// per child per day so a later phase can count attended days for billing.
// Same date + class pattern as the Daily Report tab. See DAYCARE_DIARY_DESIGN.md.

const today = () => new Date().toISOString().split('T')[0];

const DaycareAttendance = ({ apiCall, showSuccess, showError }) => {
  const [date, setDate] = useState(today());
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState('');
  const [rows, setRows] = useState([]);   // { ChildID, FirstName, LastName, Status, Reason }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadClasses = useCallback(async () => {
    try {
      const cls = await apiCall('/daycare/classes?activeOnly=1');
      setClasses(cls || []);
      setClassId((prev) => prev || (cls && cls[0] ? String(cls[0].ClassID) : ''));
    } catch (e) { showError('Error loading classes: ' + e.message); }
  }, [apiCall, showError]);

  const load = useCallback(async () => {
    if (!classId || !date) { setRows([]); return; }
    setLoading(true);
    try {
      const r = await apiCall(`/daycare/daily-report/attendance?classId=${classId}&date=${date}`);
      setRows((r || []).map((x) => ({ ...x, Status: x.Status || null, Reason: x.Reason || '' })));
    } catch (e) { showError('Error loading attendance: ' + e.message); }
    finally { setLoading(false); }
  }, [apiCall, classId, date, showError]);

  useEffect(() => { loadClasses(); }, [loadClasses]);
  useEffect(() => { load(); }, [load]);

  const setStatus = (childId, status) =>
    setRows((arr) => arr.map((r) => r.ChildID === childId ? { ...r, Status: status, Reason: status === 'Present' ? '' : r.Reason } : r));
  const setReason = (childId, reason) =>
    setRows((arr) => arr.map((r) => r.ChildID === childId ? { ...r, Reason: reason } : r));

  const markAll = (status) => setRows((arr) => arr.map((r) => ({ ...r, Status: status, Reason: status === 'Present' ? '' : r.Reason })));

  const save = async () => {
    setSaving(true);
    try {
      const entries = rows.filter((r) => r.Status).map((r) => ({ childId: r.ChildID, status: r.Status, reason: r.Reason }));
      await apiCall('/daycare/daily-report/attendance', { method: 'PUT', body: JSON.stringify({ classId: Number(classId), date, entries }) });
      showSuccess('Attendance saved');
      load();
    } catch (e) { showError('Error saving attendance: ' + e.message); }
    finally { setSaving(false); }
  };

  const present = rows.filter((r) => r.Status === 'Present').length;
  const absent = rows.filter((r) => r.Status === 'Absent').length;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm" />
        <select value={classId} onChange={(e) => setClassId(e.target.value)} className="border border-gray-300 rounded px-3 py-2 text-sm">
          <option value="">Select class…</option>
          {classes.map((c) => <option key={c.ClassID} value={c.ClassID}>{c.ClassName}{c.TeacherName ? ` · ${c.TeacherName}` : ''}</option>)}
        </select>
        <div className="flex-1" />
        {classId && rows.length > 0 && (
          <>
            <span className="text-sm text-gray-500">✅ {present} · ❌ {absent}</span>
            <Gate permission="daycare.attendance.edit" label="Mark attendance">
              <button onClick={() => markAll('Present')} className="px-3 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50">All present</button>
              <button onClick={save} disabled={saving} className="px-3 py-2 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:bg-gray-300">{saving ? 'Saving…' : 'Save'}</button>
            </Gate>
          </>
        )}
      </div>

      {!classId ? (
        <p className="text-sm text-gray-500">Pick a class to take attendance.</p>
      ) : loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">No children assigned to this class.</p>
      ) : (
        <div className="divide-y border rounded">
          {rows.map((r) => (
            <div key={r.ChildID} className="flex items-center gap-3 px-4 py-2.5 flex-wrap">
              <span className="flex-1 min-w-[140px] text-sm text-gray-800">{r.LastName} {r.FirstName}</span>
              <div className="flex gap-1">
                {[['Present', 'bg-green-600 border-green-600'], ['Absent', 'bg-red-600 border-red-600']].map(([st, on]) => (
                  <button key={st} type="button" onClick={() => setStatus(r.ChildID, r.Status === st ? null : st)}
                    className={`px-3 py-1 rounded text-sm border ${r.Status === st ? `${on} text-white` : 'border-gray-300 text-gray-600'}`}>
                    {st === 'Present' ? '✅ Present' : '❌ Absent'}
                  </button>
                ))}
              </div>
              {r.Status === 'Absent' && (
                <input value={r.Reason} onChange={(e) => setReason(r.ChildID, e.target.value)} placeholder="Reason (optional)"
                  className="border border-gray-300 rounded px-2 py-1 text-sm w-48" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DaycareAttendance;
